<template>
  <el-dialog
    v-model="_isShow"
    width="90%"
    top="0"
    lock-scroll
    center
    show-close
    destroy-on-close
    :title="adjustmentField?.label"
  >
    <div class="adjustment-popup-container">
      <div class="mb-4">
        <div class="barcode-label-container">
          {{ $t('initial_quantity') }}: {{ formatValue(customFieldData?.adjustment) }}
        </div>
      </div>
      <div class="my-4" v-for="(item, index) in tableData" :key="index">
        <div class="flex items-center w-full">
          <div
            class="tracking-record-title flex justify between items-center text-white rounded-full w-full px-4 py-3 bg-blue-400"
          >
            <div>{{ item.currentNode }} <i class="el-icon-right mx-1"></i> {{ item.nextNode }}</div>
          </div>
        </div>
        <div class="px-4">
          <div class="flex my-2">
            <span class="label-box break-all">{{ $t('adjustment_time') }}</span>
            <div class="pl-2 break-all flex items-center">
              {{ item.date }}
            </div>
          </div>
          <div class="flex my-2">
            <span class="label-box break-all">{{ $t('quantity') }}</span>
            <div class="pl-2 break-all flex items-center">
              {{ formatValue(item.startValue) }}
            </div>
          </div>
          <div class="flex my-2">
            <span class="label-box break-all">{{ $t(item.operatorType) }}</span>
            <div class="pl-2 break-all flex items-center">
              {{ formatValue(item.value) }}
            </div>
          </div>
          <div class="flex my-2">
            <span class="label-box break-all">{{ $t('remaining') }}</span>
            <div class="pl-2 break-all flex items-center">
              {{ formatValue(item.currentValue) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <el-button type="primary" class="w-full mt-4" @click="closeDialog">
      {{ $t('OK') }}
    </el-button>
  </el-dialog>
</template>

<script lang="ts">
import { IAdjustmentDisplay, ICustomFieldShowData } from '@/store/type'
import { IActivationCustomField, IBarcode, IProject, ITrackpoint } from 'smartbarcode-web-core/src/utils/types'
import { Options, Vue } from 'vue-class-component'
import { Prop, PropSync } from 'vue-property-decorator'
import { format } from 'date-fns'
import { DATE_TIME } from 'smartbarcode-web-core/src/utils/constants'
import { ECustomFieldUnitPosition } from 'smartbarcode-web-core/src/utils/enums'

@Options({
  emits: ['update:isShow'],
  name: 'AdjustmentDataDialog',
})
export default class AdjustmentDataDialog extends Vue {
  @PropSync('isShow', { type: Boolean }) _isShow!: boolean
  @Prop({ type: Object }) readonly adjustmentField!: ICustomFieldShowData

  closeDialog() {
    this._isShow = false
  }

  get barcode(): IBarcode {
    return this.$store.state.barcode?.barcode
  }

  get project(): IProject {
    return this.$store.state.project?.details
  }

  get trackPoints(): Record<string, ITrackpoint> {
    return this.project.trackPoints
  }

  get customFieldSetting(): IActivationCustomField {
    return this.project?.barcodeTypes?.[this.barcode.barcodeType]?.activationFields?.customFields?.[
      this.adjustmentField.cfKey
    ]
  }

  get customFieldData() {
    return this.barcode.activationData?.customFields?.[this.adjustmentField.cfKey]
  }

  get tableData(): IAdjustmentDisplay[] {
    const cfKey = this.adjustmentField?.cfKey || ''
    const result = [] as IAdjustmentDisplay[]
    if (this.barcode.trackingData) {
      let startValue = this.customFieldData?.adjustment || 0
      for (const tdData of this.barcode.trackingData) {
        if (tdData.customFields?.[cfKey]) {
          const value = tdData.customFields?.[cfKey]?.adjustment || 0
          const operatorType = value > 0 ? 'increase' : 'decrease'
          const currentNode = this.trackPoints?.[tdData.currentTrackPointKey].name || ''
          const nextNode = this.trackPoints?.[tdData.nextTrackPointKey].name || ''
          const currentValue = startValue + value
          result.push({
            date: format(new Date(tdData.audit?.trackedDateTime as string), DATE_TIME.WITH_SECOND),
            operatorType,
            currentNode,
            nextNode,
            startValue,
            value: Math.abs(value),
            currentValue,
          })
          startValue = currentValue
        }
      }
    }

    return result
  }

  formatValue(value?: number) {
    if (value !== undefined && value !== null) {
      if (this.customFieldSetting?.unitType && this.customFieldSetting?.position) {
        if (this.customFieldSetting.position === ECustomFieldUnitPosition.PREFIX) {
          return `${this.customFieldSetting.unitType} ${value}`
        } else if (this.customFieldSetting.position === ECustomFieldUnitPosition.SUFFIX) {
          return `${value} ${this.customFieldSetting.unitType}`
        }
      }
      return value.toString()
    }
    return ''
  }
}
</script>
<style lang="scss" scoped>
.adjustment-popup-container {
  max-height: 70vh;
  overflow-y: auto;
  .barcode-label-container {
    margin-top: 0px;
  }
  .label-box {
    font-weight: normal;
    font-size: 14px;
    width: 6rem;
  }
}
</style>
